// École Nid Douillet - News Page (Dynamic CMS Content)

import React, { useState, useEffect } from 'react';
import { CMSContent } from '../../services/cms.api';
import { cmsApi } from '../../services/cms.api';
import { CalendarIcon, TagIcon } from '@heroicons/react/24/outline';

const NewsPage: React.FC = () => {
  const [newsItems, setNewsItems] = useState<CMSContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadNewsContent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load news and announcements from CMS
        const response = await cmsApi.getPublishedContent({ language: 'fr' });
        if (response.data) {
          // Filter for news and announcements only
          const newsContent = response.data.filter(
            item => item.content_type === 'news' || item.content_type === 'announcement'
          );
          setNewsItems(newsContent);
        }
      } catch (err) {
        console.error('Error loading news content:', err);
        setError('Erreur lors du chargement des actualités. Veuillez réessayer plus tard.');
      } finally {
        setLoading(false);
      }
    };

    loadNewsContent();
  }, []);

  // Update page title
  useEffect(() => {
    document.title = 'Actualités - École Nid Douillet';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Découvrez les dernières actualités et annonces de l\'École Nid Douillet à Agadir.');
    }
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getContentTypeLabel = (type: string) => {
    switch (type) {
      case 'news':
        return 'Actualité';
      case 'announcement':
        return 'Annonce';
      default:
        return 'Article';
    }
  };

  const getContentTypeColor = (type: string) => {
    switch (type) {
      case 'news':
        return 'bg-blue-100 text-blue-800';
      case 'announcement':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="container-wide py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Chargement des actualités...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-wide py-16">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Erreur</h2>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-wide py-16">
      {/* Page Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-neutral-900 mb-6">
          Actualités de l'École
        </h1>
        <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
          Restez informé des dernières nouvelles et événements de l'École Nid Douillet
        </p>
      </div>

      {newsItems.length === 0 ? (
        <div className="text-center">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 max-w-md mx-auto">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">
              Aucune actualité disponible
            </h3>
            <p className="text-yellow-700">
              Revenez bientôt pour découvrir nos dernières nouvelles et annonces.
            </p>
          </div>
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-2">
          {newsItems.map((item) => (
            <article 
              key={item.id} 
              className="bg-white rounded-xl shadow-soft overflow-hidden hover:shadow-lg transition-shadow"
            >
              {/* Featured Image */}
              {item.featured_image && (
                <div className="aspect-w-16 aspect-h-9">
                  <img
                    src={item.featured_image}
                    alt={item.title}
                    className="w-full h-48 object-cover"
                  />
                </div>
              )}
              
              <div className="p-6">
                {/* Content Type Badge */}
                <div className="flex items-center justify-between mb-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getContentTypeColor(item.content_type)}`}>
                    <TagIcon className="h-4 w-4 mr-1" />
                    {getContentTypeLabel(item.content_type)}
                  </span>
                  
                  {/* Date */}
                  {item.published_at && (
                    <div className="flex items-center text-sm text-neutral-500">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {formatDate(item.published_at)}
                    </div>
                  )}
                </div>

                {/* Title */}
                <h2 className="text-xl font-bold text-neutral-900 mb-3 line-clamp-2">
                  {item.title}
                </h2>

                {/* Excerpt */}
                {item.excerpt && (
                  <p className="text-neutral-600 mb-4 line-clamp-3">
                    {item.excerpt}
                  </p>
                )}

                {/* Tags */}
                {item.tags && Array.isArray(item.tags) && item.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {item.tags.slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-neutral-100 text-neutral-700"
                      >
                        {tag}
                      </span>
                    ))}
                    {item.tags.length > 3 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-neutral-100 text-neutral-700">
                        +{item.tags.length - 3} autres
                      </span>
                    )}
                  </div>
                )}

                {/* Read More Button */}
                <div className="pt-4 border-t border-neutral-100">
                  <button
                    onClick={() => {
                      // Create a modal or navigate to full article view
                      // For now, we'll show an alert with the content
                      alert(`Contenu complet:\n\n${item.content.replace(/<[^>]*>/g, '')}`);
                    }}
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium transition-colors"
                  >
                    Lire la suite
                    <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Contact CTA */}
      <div className="mt-16 text-center">
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-neutral-900 mb-4">
            Restez connecté avec nous
          </h3>
          <p className="text-neutral-700 mb-6 max-w-2xl mx-auto">
            Ne manquez aucune actualité de l'École Nid Douillet. Contactez-nous pour plus d'informations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
            >
              Nous contacter
            </a>
            <a 
              href="/" 
              className="inline-flex items-center px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg border border-primary-600 hover:bg-primary-50 transition-colors"
            >
              Retour à l'accueil
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsPage;
